import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartNodeAdapter,
  ProcessFlowchartTaskSchemaNodeAdapter,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {
  FlowchartNode,
  isCollapsedStaticNodeData,
  isInteractiveTaskNode,
  isStaticPlaceholderNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from './processFlowchartConfigEditorNodeTypes';

export function getAdapterForNode(
  node: FlowchartNode | null
): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | ProcessFlowchartNodeAdapter | null {
  return getAdapterForNodeData(node?.data ?? null);
}

export function getAdapterForNodeData(
  nodeData: StaticFlowchartNodeData | null
): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | ProcessFlowchartNodeAdapter | null {
  if (nodeData == null) {
    return null;
  }
  if (isStaticTaskSchemaNodeData(nodeData)) {
    if (nodeData.taskDefinitionVersionSpec == null) {
      return null;
    }
    return PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      nodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
  }
  if (isStaticPlaceholderNodeData(nodeData)) {
    return null;
  }
  if (isCollapsedStaticNodeData(nodeData)) {
    return null;
  }
  return PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(nodeData.nodeType);
}

export function getAdapterForTaskSchemaNode(
  node: FlowchartNode | null
): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | null {
  if (node && isInteractiveTaskNode(node) && node.data.taskDefinitionVersionSpec != null) {
    return PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      node.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
  }
  return null;
}

export function isStartOrEndNode(nodeDetailsType: ProcessFlowchartNodeDetailsType): boolean {
  return (
    nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
    nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  );
}

export function isNodeDeletable(
  node: FlowchartNode,
  options?: {allowStartEndNodeDeletion?: boolean}
): boolean {
  if (
    isInteractiveTaskNode(node) &&
    node.data.isInaccessible &&
    node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS
  ) {
    return true;
  }
  if (
    options?.allowStartEndNodeDeletion &&
    isInteractiveTaskNode(node) &&
    isStartOrEndNode(node.data.nodeDetailsType)
  ) {
    return true;
  }
  const adapter = getAdapterForNode(node);
  if (adapter == null) {
    return true;
  }
  return adapter.isDeletable();
}

/**
 * Returns true when the node is a task-schema node that already has at least one task created
 * against it (any status). In PCM mode this blocks deletion to avoid orphaning execution data.
 */
export function nodeHasExistingTasks(node: FlowchartNode): boolean {
  if (!isInteractiveTaskNode(node)) {
    return false;
  }
  return (node.data.statusCounts ?? []).length > 0;
}
